import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Mui Styles
import { StyledPaper } from "@/styles/mui/globalStyles";
import { TableListTheme } from "@/styles/mui/tableListTheme";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Mui
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";

// Components
import DataTable from "@/components/dataTable";
import DateSelection from "@/components/dateSelection";
import Layout from "@/components/layout";
import Title from "@/components/title";

// Moment
import moment from "moment";
import "moment/locale/zh-hk";
import { useSelector } from "react-redux";
// Zoom Images
import ImageZoom from "@/components/imageZoom";
import "react-medium-image-zoom/dist/styles.css";
// Fetch
import {
  getGiveawayListByCustId,
  getImagesKeyById,
} from "@/fetch/fetchGiveaway";

// Config
import { CustomerDetailColumn } from "@/config/tableColumnConfig";

const CustomerManageDetailPage = (props) => {
  const { open, setOpen } = props;

  // Theme
  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            marginBottom: 14,
          },
        },
      },
    },
  });

  const { id } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const [dataTableLoading, setDataTableLoading] = React.useState(false);
  const [giveawayData, setGiveawayData] = React.useState([]);
  const [imagesList, setImagesList] = React.useState([]);
  const brand = location?.state?.criteria.brand?.item_brand;
  const searchText = location?.state?.criteria.product;
  const defaultStartDate = moment().startOf("month");
  const defaultEndDate = moment().endOf("month");
  const startDate = useSelector(
    (state) =>
      state.system.selectedDate[pathname]?.startDate ?? defaultStartDate
  );
  const endDate = useSelector(
    (state) => state.system.selectedDate[pathname]?.endDate ?? defaultEndDate
  );
  const navigate = useNavigate();

  const loadImages = async (giveaway_invoice_id) => {
    await getImagesKeyById(giveaway_invoice_id).then((data) => {
      let urls = [];
      if (data.results) {
        // eslint-disable-next-line no-unused-vars
        for (const [key, value] of Object.entries(data.data)) {
          urls.push(`${value}`);
        }
        setImagesList(urls);
      }
    });
  };

  const fetchAll = async () => {
    try {
      setDataTableLoading(true);

      await getGiveawayListByCustId(
        id,
        startDate,
        endDate,
        brand,
        searchText
      ).then((data) => {
        if (data && data.results) {
          setGiveawayData(data.data);
        } else {
          setGiveawayData([]);
        }
      });
    } catch (error) {
    } finally {
      setDataTableLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  moment.updateLocale("zh-hk", {
    weekdaysShort: ["日", "一", "二", "三", "四", "五", "六"],
  });

  return (
    <Layout header="CustomerManageDetailPage" open={open} setOpen={setOpen}>
      <Title
        title={`管理顧客 / ${parseInt(id) === 0 ? "免登記" : "修改顧客"} `}
        history={() =>
          //save current route to let upper page "/customerManage" know what is the route name of this page when back to page "/customerManage"
          navigate("/customerManage", {
            state: { ...location.state, from: location.pathname },
          })
        }
      />
      <Grid container spacing={2}>
        {parseInt(id) !== 0 && (
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 6,
            }}
          >
            <StyledPaper page={"yes"} alignItems={"left"}>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                    lg: 4,
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    component="div"
                    sx={{
                      margin: "8px 0px",
                      lineHeight: "1.5",
                      fontSize: "0.75rem",
                      fontWeight: "400",
                      color: "rgb(145, 158, 171)",
                      width: "100%",
                    }}
                  >
                    顧客全名
                  </Typography>

                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{ fontWeight: "bold", width: "100%" }}
                  >
                    {giveawayData.fullname}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                    lg: 4,
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    component="div"
                    sx={{
                      margin: "8px 0px",
                      lineHeight: "1.5",
                      fontSize: "0.75rem",
                      fontWeight: "400",
                      color: "rgb(145, 158, 171)",
                      width: "100%",
                    }}
                  >
                    顧客電話
                  </Typography>
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{ fontWeight: "bold", width: "100%" }}
                  >
                    {giveawayData.phone}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                    lg: 4,
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    component="div"
                    sx={{
                      margin: "8px 0px",
                      lineHeight: "1.5",
                      fontSize: "0.75rem",
                      fontWeight: "400",
                      color: "rgb(145, 158, 171)",
                      width: "100%",
                    }}
                  >
                    願意收取推廣資料？
                  </Typography>
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{ fontWeight: "bold", width: "100%" }}
                  >
                    {giveawayData.opt_out ? "願意" : "不願意"}
                  </Typography>
                </Grid>
              </Grid>
            </StyledPaper>
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            md: parseInt(id) === 0 ? 12 : 6,
            lg: parseInt(id) === 0 ? 12 : 6,
          }}
        >
          <StyledPaper page={"yes"} alignItems={"left"}>
            <ThemeProvider theme={theme}>
              <Grid container spacing={2}>
                <DateSelection
                  defaultStartDate={defaultStartDate}
                  defaultEndDate={defaultEndDate}
                ></DateSelection>
              </Grid>
            </ThemeProvider>
          </StyledPaper>
        </Grid>
        <Grid size={12}>
          <StyledPaper page={"yes"}>
            <ThemeProvider theme={TableListTheme}>
              <DataTable
                loading={dataTableLoading}
                getRowId={(row) => row.invoiceitem_id}
                rows={giveawayData.items ? giveawayData.items : []}
                columns={CustomerDetailColumn}
                pageSize={100}
                onCellClick={(data) => loadImages(data.row.giveaway_invoice_id)}
              />
            </ThemeProvider>
          </StyledPaper>
          {imagesList.length > 0 && (
            <StyledPaper page={"yes"}>
              <ImageZoom attachments={imagesList}></ImageZoom>
            </StyledPaper>
          )}
        </Grid>
      </Grid>
    </Layout>
  );
};

export default CustomerManageDetailPage;
